/**
 * The score series for one influence level: a line per scoring-method
 * version with its confidence band, and the sample size under every week.
 *
 * A week with no reading is a null in the data, so the line breaks there and
 * never touches the axis. Two versions are two lines, never one trend, and the
 * week a new version starts is marked.
 */

import type { ReactNode } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  XAxis,
  YAxis,
  type DotItemDotProps,
  type XAxisTickContentProps,
} from 'recharts';

import { getLocale } from '../../../i18n';
import { count, interval, score } from '../../../chrome/measure/format';
import { mt } from '../../../chrome/measure/messages';
import { ChartContainer, ChartTooltip, type ChartConfig } from '../../../ui/chart';
import { st2 } from './messages';
import { chartRows, versionsOf, type LevelSeries, type SeriesPoint } from './series';

const VERSION_COLORS = ['var(--chart-1)', 'var(--chart-2)', 'var(--chart-3)', 'var(--chart-4)'];

function weekLabel(week: string): string {
  return new Intl.DateTimeFormat(getLocale(), {
    day: '2-digit',
    month: '2-digit',
    timeZone: 'UTC',
  }).format(new Date(`${week}T00:00:00Z`));
}

function pointLabel(point: SeriesPoint): string {
  const locale = getLocale();
  return st2('series.point', {
    week: weekLabel(point.week),
    score: score(point.score, locale),
    sample: mt('ncs.sample', { n: count(point.n, locale) }),
    interval: mt('ncs.interval', { interval: interval(point.low, point.high, locale) }),
  });
}

export function NcsSeries(props: { series: LevelSeries }): ReactNode {
  const { series } = props;
  const locale = getLocale();
  const versions = versionsOf(series);
  const rows = chartRows(series);
  const pointOf = new Map(rows.map((row) => [row.week, row.point]));

  const data = rows.map(({ week, point }) => {
    const datum: Record<string, unknown> = { week };
    versions.forEach((version, index) => {
      const own = point !== null && point.version === version ? point : null;
      datum[`score${index}`] = own === null ? null : own.score;
      datum[`band${index}`] = own === null ? null : [own.low, own.high];
    });
    return datum;
  });

  const config: ChartConfig = Object.fromEntries(
    versions.map((version, index) => [
      `score${index}`,
      {
        label: st2('series.methodValue', { version }),
        color: VERSION_COLORS[index % VERSION_COLORS.length],
      },
    ]),
  );

  function Tick(tick: XAxisTickContentProps): ReactNode {
    const week = String(tick.payload.value);
    const point = pointOf.get(week) ?? null;
    return (
      <g transform={`translate(${tick.x},${tick.y})`}>
        <text textAnchor="middle" dy={12} className="fill-foreground">
          {weekLabel(week)}
        </text>
        <text textAnchor="middle" dy={26} data-numeric className="fill-muted-foreground">
          {point === null
            ? st2('series.noReading')
            : mt('ncs.sample', { n: count(point.n, locale) })}
        </text>
      </g>
    );
  }

  function Dot(dot: DotItemDotProps): ReactNode {
    const point = pointOf.get(String((dot.payload as { week: string }).week)) ?? null;
    if (point === null || dot.cx === undefined || dot.cy === undefined) return null;
    return (
      <circle
        key={point.week}
        cx={dot.cx}
        cy={dot.cy}
        r={3}
        role="img"
        aria-label={pointLabel(point)}
        fill={dot.stroke}
      />
    );
  }

  return (
    <ChartContainer
      config={config}
      role="img"
      aria-label={st2('series.label')}
      className="aspect-auto h-64 w-full"
    >
      <ComposedChart data={data} margin={{ bottom: 16 }} accessibilityLayer={false}>
        <CartesianGrid vertical={false} />
        <XAxis
          dataKey="week"
          tickLine={false}
          axisLine={false}
          interval={0}
          tick={Tick}
          height={40}
          padding={{ left: 32, right: 32 }}
        />
        <YAxis domain={[-100, 100]} tickLine={false} axisLine={false} width={40} />
        <ChartTooltip
          cursor={false}
          content={({ active, label }) => {
            const point = active ? (pointOf.get(String(label)) ?? null) : null;
            return point === null ? null : (
              <div
                data-numeric
                className="rounded-lg border bg-background px-2.5 py-1.5 text-caption shadow-md"
              >
                {pointLabel(point)}
              </div>
            );
          }}
        />
        <ReferenceLine y={0} stroke="var(--border)" />
        {series.versionChanges.map((change) => (
          <ReferenceLine
            key={change.week}
            x={change.week}
            stroke="var(--muted-foreground)"
            strokeDasharray="4 4"
            label={{
              value: st2('series.versionChange', { from: change.from, to: change.to }),
              position: 'insideTopRight',
              className: 'fill-muted-foreground',
            }}
          />
        ))}
        {versions.map((version, index) => (
          <Area
            key={`band-${version}`}
            dataKey={`band${index}`}
            stroke="none"
            fill={VERSION_COLORS[index % VERSION_COLORS.length]}
            fillOpacity={0.15}
            connectNulls={false}
            isAnimationActive={false}
            activeDot={false}
          />
        ))}
        {versions.map((version, index) => (
          <Line
            key={`score-${version}`}
            dataKey={`score${index}`}
            stroke={VERSION_COLORS[index % VERSION_COLORS.length]}
            strokeWidth={2}
            dot={Dot}
            connectNulls={false}
            isAnimationActive={false}
          />
        ))}
      </ComposedChart>
    </ChartContainer>
  );
}
